import { createElement } from 'react';
import { type Icon as FeatherIcon } from 'react-feather';
import { captureException } from '@sentry/core';

export type CustomIconName =
  | 'OtherDeciduous'
  | 'OtherEvergreen'
  | 'Help'
  | 'HelpGray'
  | 'Up'
  | 'Down'
  | 'Cross'
  | 'HazardTrees'
  | 'DecliningTrees'
  | 'StrikeTrees'
  | 'Pin'
  | 'ActionDots'
  | 'WorkCompleted'
  | 'NoWorkNeeded'
  | 'WorkNeeded'
  | 'MapLegend'
  | 'InfoFilled'
  | 'InfoOutline'
  | 'Distribution'
  | 'Transmission'
  | 'Options'
  | 'PinAlt'
  | 'Pole'
  | 'Span'
  | 'Historical'
  | 'RedFlagWarning'
  | 'MapLink'
  | 'HoverPin'
  | 'HelpOutline'
  | 'ObjectiveEncroachment'
  | 'ObjectiveFallIn'
  | 'ObjectiveReliability'
  | 'ObjectiveWildfire'
  | 'ToggleSmall'
  | 'Flame'
  | 'Detected'
  | 'Fulcrum';

interface BaseIconProps {
  /**
   * Size of the icon
   */
  size?: number;
  /**
   * Icon outline color
   */
  color?: string;
  /**
   * Size of the icon
   */
  strokeWidth?: number;
  children?: React.ReactNode;
}

type IconProps =
  | (BaseIconProps & { name: CustomIconName; Component?: never })
  | (BaseIconProps & { Component: FeatherIcon; name?: never });

export const Icon = ({
  name,
  Component,
  size = 20,
  color = 'black',
  ...props
}: IconProps) => {
  if (Component) {
    return (
      <Component color={color} size={size} {...props}>
        {props.children}
      </Component>
    );
  }

  switch (name) {
    case 'OtherDeciduous':
      return (
        <img
          src="/icons/deciduous.png"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Deciduous"
          width={size}
        />
      );
    case 'OtherEvergreen':
      return (
        <img
          src="/icons/evergreen.png"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Evergreen"
          width={size}
        />
      );
    case 'Help':
      return (
        <img
          src="/icons/Help.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Help"
          width={size}
        />
      );
    case 'HelpGray':
      return (
        <img
          src="/icons/helpGray.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Help"
          width={size}
        />
      );
    case 'Up':
      return (
        <img
          src="/icons/Up.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Up"
          width={size}
        />
      );
    case 'Down':
      return (
        <img
          src="/icons/Down.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Down"
          width={size}
        />
      );
    case 'Cross':
      return (
        <img
          src="/icons/cross.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="cross"
          width={size}
        />
      );
    case 'HazardTrees':
      return (
        <img
          src="/icons/hazardTrees.png"
          style={{ verticalAlign: 'text-bottom' }}
          alt="hazardTrees"
          width={size}
        />
      );
    case 'DecliningTrees':
      return (
        <img
          src="/icons/decliningTrees.png"
          style={{ verticalAlign: 'text-bottom' }}
          alt="decliningTrees"
          width={size}
        />
      );
    case 'StrikeTrees':
      return (
        <img
          src="/icons/strikeTrees.png"
          style={{ verticalAlign: 'text-bottom' }}
          alt="strikeTrees"
          width={size}
        />
      );
    case 'Pin':
      return (
        <img
          src="/icons/pin.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="location pin"
          width={size}
        />
      );
    case 'ActionDots':
      return (
        <img
          src="/icons/more-horizontal.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="action dots"
          width={size}
        />
      );
    case 'WorkCompleted':
      return (
        <img
          src="/icons/work-completed.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="work completed"
          width={size}
        />
      );
    case 'NoWorkNeeded':
      return (
        <img
          src="/icons/no-work-needed.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="no work needed"
          width={size}
        />
      );
    case 'WorkNeeded':
      return (
        <img
          src="/icons/work-needed.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="work needed"
          width={size}
        />
      );
    case 'MapLegend':
      return (
        <img
          src="/icons/map-legend.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Map legend"
          width={size}
        />
      );
    case 'InfoFilled':
      return (
        <img
          src="/icons/info-filled.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Info"
          width={size}
        />
      );
    case 'InfoOutline':
      return (
        <img
          src="/icons/info-outline.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Info"
          width={size}
        />
      );
    case 'Distribution':
      return <Distribution name="Distribution" color={color} size={size} />;
    case 'Transmission':
      return <Transmission name="Transmission" color={color} size={size} />;
    case 'Options':
      return <Options name="Options" color={color} size={size} />;
    case 'PinAlt':
      return (
        <img
          src="/icons/pin-alt.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Pin"
          width={size}
        />
      );
    case 'Pole':
      return (
        <img
          src="/icons/pole.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Pole"
          width={size}
        />
      );
    case 'Span':
      return (
        <img
          src="/icons/span.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Span"
          width={size}
        />
      );
    case 'Historical':
      return (
        <img
          src="/icons/historical.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Historical analysis"
          width={size}
        />
      );
    case 'RedFlagWarning':
      return (
        <img
          src="/icons/red-flag-warning.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Red Flag Warning"
          width={size}
        />
      );
    case 'MapLink':
      return (
        <svg
          fill={color}
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          style={{ verticalAlign: 'text-bottom' }}
        >
          <path d="m7.6 1 .8.1 5 2.5a1 1 0 0 1-.8 1.8L8 3 2 6.6v13.7L7.5 17h.1c.3-.1.6-.1.8 0L16 21l6-3.5V12a1 1 0 1 1 2 0v6c0 .4-.2.7-.5.9l-7 4a1 1 0 0 1-1 0L8 19 1.5 23A1 1 0 0 1 0 22V6c0-.4.2-.7.5-.9l7-4h.1Z" />
          <path d="M7 18V2a1 1 0 0 1 2 0v16a1 1 0 1 1-2 0ZM15 22v-8a1 1 0 1 1 2 0v8a1 1 0 1 1-2 0ZM22 7V2h-5a1 1 0 1 1 0-2h6c.6 0 1 .4 1 1v6a1 1 0 1 1-2 0Z" />
          <path d="M22.3.3a1 1 0 1 1 1.4 1.4l-9 9a1 1 0 0 1-1.4-1.4l9-9Z" />
        </svg>
      );
    case 'Flame':
      return (
        <svg
          fill={color}
          width={(size * 9) / 12}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 9 12"
          style={{ verticalAlign: 'text-bottom' }}
        >
          <path d="M3.35335 0.201209C4.02646 -0.422208 5.27475 0.524248 5.58479 1.38773C5.7401 1.82018 5.98436 2.28705 6.35628 2.77152C6.64564 3.14826 6.9958 3.4729 7.36214 3.77543C8.52177 4.73303 9.17532 6.24976 8.95882 7.84965C8.73877 9.47602 7.67459 10.78 6.26839 11.3828C6.27774 11.3093 6.28499 11.2343 6.28499 11.1582C6.28478 10.1857 5.49584 9.39751 4.52327 9.3975C3.55069 9.3975 2.76177 10.1857 2.76155 11.1582C2.76155 11.2417 2.76985 11.3239 2.78108 11.4043C1.02909 10.6774 -0.129193 8.87615 0.0115516 6.92972C0.0513734 6.38036 0.186726 5.86359 0.399247 5.39359C0.506708 5.15733 0.978607 5.26821 1.06624 5.51273C1.12391 5.67413 1.18864 5.82563 1.2469 5.95219C1.34488 6.16509 1.56964 6.28857 1.80159 6.25492C2.13441 6.20641 2.34775 5.85808 2.21761 5.54789C1.80959 4.57512 1.78206 3.67777 1.78206 3.67777L1.78011 3.66215C1.80283 2.61638 2.14166 1.32351 3.35335 0.201209Z" />
        </svg>
      );
    case 'Detected':
      return (
        <svg
          fill={color}
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fillRule="evenodd"
          clipRule="evenodd"
          style={{ verticalAlign: 'text-bottom' }}
        >
          <path d="M7.86666 12.7427C7.49847 12.7427 7.2 12.4442 7.2 12.076L7.2 8.66666C7.2 8.29847 7.49847 7.99999 7.86666 7.99999C8.23485 7.99999 8.53333 8.29847 8.53333 8.66666L8.53333 12.076C8.53333 12.4442 8.23485 12.7427 7.86666 12.7427Z" />
          <path d="M4.84598 5.58774C5.17594 4.24045 6.3906 3.24111 7.8395 3.24111C9.2884 3.24111 10.5031 4.24046 10.833 5.58775C11.7515 6.1206 12.3706 7.115 12.3706 8.25522C12.3706 9.95731 10.9907 11.3371 9.28865 11.3371C8.76545 11.3371 8.27172 11.2063 7.8395 10.9757C7.40728 11.2063 6.91354 11.3371 6.39034 11.3371C4.68825 11.3371 3.30842 9.95731 3.30842 8.25522C3.30842 7.115 3.92752 6.12059 4.84598 5.58774ZM7.8395 4.57444C6.9452 4.57444 6.20686 5.24638 6.10338 6.11259C6.07511 6.34922 5.92268 6.55276 5.70356 6.64647C5.07801 6.914 4.64176 7.53442 4.64176 8.25522C4.64176 9.22093 5.42463 10.0038 6.39034 10.0038C6.78476 10.0038 7.14693 9.87402 7.43911 9.65455C7.67631 9.47639 8.00269 9.47639 8.23988 9.65455C8.53207 9.87402 8.89424 10.0038 9.28865 10.0038C10.2544 10.0038 11.0372 9.22093 11.0372 8.25522C11.0372 7.53442 10.601 6.914 9.97543 6.64647C9.75632 6.55276 9.60389 6.34922 9.57562 6.11259C9.47214 5.24639 8.7338 4.57444 7.8395 4.57444Z" />
          <path d="M10.6667 0.666667C10.6667 0.298477 10.9651 0 11.3333 0H15.3333C15.7015 0 16 0.298477 16 0.666667V4.66667C16 5.03486 15.7015 5.33333 15.3333 5.33333C14.9651 5.33333 14.6667 5.03486 14.6667 4.66667V1.33333H11.3333C10.9651 1.33333 10.6667 1.03486 10.6667 0.666667Z" />
          <path d="M10.6667 15.3333C10.6667 15.7015 10.9651 16 11.3333 16H15.3333C15.7015 16 16 15.7015 16 15.3333V11.3333C16 10.9651 15.7015 10.6667 15.3333 10.6667C14.9651 10.6667 14.6667 10.9651 14.6667 11.3333V14.6667H11.3333C10.9651 14.6667 10.6667 14.9651 10.6667 15.3333Z" />
          <path d="M5.33333 0.666667C5.33333 0.298477 5.03486 0 4.66667 0H0.666667C0.298477 0 0 0.298477 0 0.666667V4.66667C0 5.03486 0.298477 5.33333 0.666667 5.33333C1.03486 5.33333 1.33333 5.03486 1.33333 4.66667V1.33333H4.66667C5.03486 1.33333 5.33333 1.03486 5.33333 0.666667Z" />
          <path d="M5.33333 15.3333C5.33333 15.7015 5.03486 16 4.66667 16H0.666667C0.298477 16 0 15.7015 0 15.3333V11.3333C0 10.9651 0.298477 10.6667 0.666667 10.6667C1.03486 10.6667 1.33333 10.9651 1.33333 11.3333V14.6667H4.66667C5.03486 14.6667 5.33333 14.9651 5.33333 15.3333Z" />
        </svg>
      );
    case 'HoverPin':
      return (
        <img
          src="/icons/pin.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Hover Pin"
          width={size}
        />
      );
    case 'HelpOutline':
      return (
        <img
          src="/icons/help-outline.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Help"
          width={size}
        />
      );
    case 'ObjectiveEncroachment':
      return (
        <img
          src="/icons/objective-encroachment.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Encroachment"
          width={size}
        />
      );
    case 'ObjectiveFallIn':
      return (
        <img
          src="/icons/objective-fall-in.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Fall-in"
          width={size}
        />
      );
    case 'ObjectiveReliability':
      return (
        <img
          src="/icons/objective-reliability.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Reliability"
          width={size}
        />
      );
    case 'ObjectiveWildfire':
      return (
        <img
          src="/icons/objective-wildfire.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Wildfire"
          width={size}
        />
      );
    case 'ToggleSmall':
      return (
        <img
          src="/icons/toggle-small.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt="Toggle"
          width={size}
        />
      );
    case 'Fulcrum':
      return (
        <img
          src="/icons/fulcrum.svg"
          style={{ verticalAlign: 'text-bottom' }}
          alt=""
          width={size}
        />
      );
    default:
      captureException(`Icon ${name} not found.`);
      return null;
  }
};

const Options = ({ color = 'black', ...props }: IconProps) => {
  return createElement(
    'svg',
    {
      stroke: color,
      width: props.size,
      height: props.size,
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: '0 0 24 24',
      fill: 'none',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    createElement('path', {
      d: 'M10 10C11.6569 10 13 8.65685 13 7C13 5.34315 11.6569 4 10 4C8.34315 4 7 5.34315 7 7C7 8.65685 8.34315 10 10 10Z',
    }),
    createElement('path', {
      d: 'M14 20C15.6569 20 17 18.6569 17 17C17 15.3431 15.6569 14 14 14C12.3431 14 11 15.3431 11 17C11 18.6569 12.3431 20 14 20Z',
    }),
    createElement('path', { d: 'M3 7L7 7' }),
    createElement('path', { d: 'M3 17L11 17' }),
    createElement('path', { d: 'M13 7L20 7' }),
    createElement('path', { d: 'M17 17L21 17' })
  );
};

const Transmission = ({ color = 'black', ...props }: IconProps) => {
  return createElement(
    'svg',
    {
      stroke: color,
      width: props.size,
      height: props.size,
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: '0 0 24 24',
      fill: 'none',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    createElement('path', { d: 'M20 21L7 13' }),
    createElement('path', { d: 'M4 21L17 13' }),
    createElement('path', { d: 'M18 6L6 6' }),
    createElement('path', { d: 'M14 3L10 3' }),
    createElement('path', { d: 'M3 9L21 9' }),
    createElement('path', { d: 'M10 3L4 21' }),
    createElement('path', { d: 'M14 3L20 21' })
  );
};

const Distribution = ({ color = 'black', ...props }: IconProps) => {
  return createElement(
    'svg',
    {
      stroke: color,
      width: props.size,
      height: props.size,
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: '0 0 24 24',
      fill: 'none',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    createElement('path', { d: 'M7 6H17' }),
    createElement('path', { d: 'M10 9H14' }),
    createElement('path', { d: 'M12 3L12 21' })
  );
};
